/**
 * Plain-language narration of a chart's data query. The Copilot never writes SQL: it picks a chart
 * spec, and the backend maps that to a fixed catalog query. This turns the chosen spec into a sentence
 * a researcher can read without exposing database details in the chat.
 */
import type { ChartSpec, Range } from './copilotTypes';

// Human-readable metric labels. Falls back to a humanized key for anything not listed.
const METRIC_LABELS: Record<string, string> = {
  steps_total: 'steps',
  active_energy_burned_total: 'active energy burned',
  resting_heart_rate: 'resting heart rate',
  exercise_time_total: 'exercise time',
  sleep_duration: 'sleep duration',
  vo2_max: 'VO₂ max',
  distance_walking_running_total: 'walking + running distance',
  hrv_sdnn: 'HRV (SDNN)',
  hrv_rmssd: 'HRV (RMSSD)',
  walking_heart_rate_avg: 'walking heart rate',
  oxygen_saturation_avg: 'oxygen saturation',
  respiratory_rate_avg: 'respiratory rate',
  body_mass: 'body mass',
  bmi: 'BMI',
};

function metricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric.replace(/_(total|avg)$/, '').replace(/_/g, ' ');
}

const RANGE_LABELS: Record<string, string> = {
  '7d': 'the last 7 days',
  '30d': 'the last 30 days',
  '90d': 'the last 90 days',
  '6m': 'the last 6 months',
  '1y': 'the last year',
  all: 'all available data',
};

function rangeLabel(range: Range): string {
  if (typeof range === 'string') return RANGE_LABELS[range] ?? range;
  return `${range.from} to ${range.to}`;
}

const STAT_LABELS: Record<string, string> = {
  avg: 'average', max: 'maximum', min: 'minimum', median: 'median', total: 'total',
};

/**
 * A one-sentence, end-user-facing description of the data a chart spec pulls. Names what the
 * researcher controls (metrics, period), not table/column internals.
 */
export function describeQuery(spec: ChartSpec): string {
  const over = (range: Range) => `over ${rangeLabel(range)}`;
  switch (spec.chart) {
    case 'histogram':
      return `Distribution of daily ${metricLabel(spec.metric)} ${over(spec.range)}.`;
    case 'kpi':
      return `Latest ${spec.metrics.map(metricLabel).join(', ')} ${over(spec.range)}.`;
    case 'calendar_heatmap':
      return `Daily ${metricLabel(spec.metric)}, day by day ${over(spec.range)}.`;
    case 'trend_line': {
      const extras = [
        spec.rollingAvgDays ? 'a rolling average' : null,
        spec.trendline ? 'a fitted trend line' : null,
      ].filter(Boolean);
      const tail = extras.length ? `, with ${extras.join(' and ')}` : '';
      return `Daily ${metricLabel(spec.metric)} ${over(spec.range)}${tail}.`;
    }
    case 'scatter_regression':
      return `${metricLabel(spec.xMetric)} against ${metricLabel(spec.yMetric)} ${over(spec.range)}, with a regression fit.`;
    case 'stacked_area':
      return `Sleep stages per night ${over(spec.range)}.`;
    case 'polar_bar':
      return `${metricLabel(spec.metric)} by day of week ${over(spec.range)}.`;
    case 'bubble':
      return `${metricLabel(spec.xMetric)} vs ${metricLabel(spec.yMetric)}, sized by ${metricLabel(spec.sizeMetric)}, ${over(spec.range)}.`;
    case 'stat':
      return `${STAT_LABELS[spec.stat] ?? spec.stat} ${metricLabel(spec.metric)} ${over(spec.range)}.`;
    default:
      return 'Querying the study’s health records.';
  }
}
